import React, { useState } from "react";
import classNames from "classnames";
import Link from "next/link";
// eslint-disable-next-line no-restricted-imports -- TODO: update to `next/navigation` before using this component in App Router
import { useRouter } from "next/router";

import { ProductMarketplaceCategory } from "@/custom-types/Product";
import { useSelector } from "@/redux/hooks";
import { getDomainCountryCode } from "@/redux/selectors/config";

import SolidArrowIcon from "@/components/Icons/arrows/SolidArrowIcon";
import CloseIcon from "@/components/Icons/x.svg";

import { GTM_EVENTS, trackGtmEvent } from "../lib/gtm";
import { hasIosParam } from "../lib/paramCheck";

export const NAV_LINKS = [
  { href: "/products/cannabis/flower", name: "Flower" },
  { href: "/products/delta-8-thc", name: "Delta-8" },
  { href: "/products/edibles", name: "Edibles" },
  {
    hideFromIOS: true,
    href: "/products/concentrates/cartridges",
    name: "Cartridges",
  },
  {
    hideFromIOS: true,
    hideFromUS: true,
    href: "/products/vaping",
    name: "Vaping",
  },
  {
    hideFromCA: true,
    hideFromIOS: true,
    href: "/products/vaping/vape-pens",
    name: "Weed pens",
  },
  { href: "/products/concentrates", name: "Concentrates" },
  { href: "/products/cannabis/prerolls", name: "Pre-rolls" },
  { href: "/products/hemp-cbd", name: "Hemp CBD" },
  { href: "/products/growing", name: "Growing" },
  { href: "/products/cannabis/seeds", name: "Seeds" },
];

const Overlay = ({
  isSideNavOpen,
  setSideNavOpen,
}: {
  isSideNavOpen: boolean;
  setSideNavOpen: (value: boolean) => void;
}) => (
  <div
    className={classNames(
      "fixed inset-0 z-above-header transition-[opacity] motion-reduce:transition-none bg-black bg-opacity-50",
      {
        "opacity-0 pointer-events-none invisible": !isSideNavOpen,
        "opacity-1 pointer-events-auto visible": isSideNavOpen,
      },
    )}
    onClick={() => setSideNavOpen(false)}
    onKeyDown={() => setSideNavOpen(false)}
    role="button"
    tabIndex={0}
    data-testid="sidenav-overlay"
    aria-label="close side nav"
  />
);

const SideNavigation = ({
  categories,
  currentCategory,
  isSideNavOpen,
  setSideNavOpen,
}: {
  categories: ProductMarketplaceCategory[];
  currentCategory?: ProductMarketplaceCategory;
  isSideNavOpen: boolean;
  setSideNavOpen: (value: boolean) => void;
}) => {
  const router = useRouter();
  const domainCountryCode = useSelector(getDomainCountryCode);

  const localeCategories = categories.filter(
    (category) => !(domainCountryCode === "CA" && category.slug === "hemp-cbd"),
  );

  const isIos = hasIosParam(router);
  const localeDeviceCategories = localeCategories.filter(
    (category) => !(isIos && category.slug === "vaping"),
  );

  const onClick = (
    eventName: keyof typeof GTM_EVENTS.TOP_NAV,
    setSideNav: boolean,
    eventLabel?: string,
  ) => {
    setSideNavOpen(setSideNav);
    trackGtmEvent(GTM_EVENTS.TOP_NAV[eventName], { eventLabel });
  };

  return (
    <div
      className={classNames(
        "h-full min-h-screen w-[320px] transition-transform z-above-header bg-white left-0 top-0 fixed",
        {
          "translate-x-0": isSideNavOpen,
          "translate-x-[-320px]": !isSideNavOpen,
        },
      )}
      data-testid="sidenav"
    >
      <div className="flex justify-between items-center p-lg border-b border-light-grey">
        <h2 className="text-xs font-extrabold uppercase">Browse by category</h2>
        <button
          data-testid="sidenav-close-button"
          aria-label="Close product category navigation"
          onClick={() => setSideNavOpen(false)}
        >
          <CloseIcon width={24} height={24} />
        </button>
      </div>
      <ul className="flex flex-col gap-lg overflow-y-scroll h-[calc(100%-56px)] p-lg text-green">
        {localeDeviceCategories.map((category: ProductMarketplaceCategory) => {
          const href = `/products/${category.slug}`;
          const currentDeviceSubcategories = category.subcategories.filter(
            (subcategory) => !(isIos && subcategory.slug === "cartridges"),
          );

          return (
            <li className="mb-xs" key={category.slug}>
              <Link
                data-testid="sidenav-link"
                href={href}
                onClick={() => onClick("DROPDOWN_CLICK", false, href)}
                className={classNames({
                  "font-bold": category.slug === currentCategory?.slug,
                })}
              >
                {category.name}
              </Link>
              {currentDeviceSubcategories.length ? (
                <ul className="flex flex-col gap-md mt-md">
                  {currentDeviceSubcategories.map((subcategory) => {
                    const href = `/products/${category.slug}/${subcategory.slug}`;

                    return (
                      <li key={subcategory.slug} className="mb-xs ml-lg">
                        <Link
                          className={classNames({
                            "font-bold":
                              subcategory.slug === currentCategory?.slug,
                          })}
                          data-testid="sidenav-link"
                          href={href}
                          onClick={() => onClick("DROPDOWN_CLICK", false, href)}
                        >
                          {subcategory.name}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <></>
              )}
            </li>
          );
        })}
        <li className="mb-xs">
          <Link href="/brands">All brands</Link>
        </li>
      </ul>
    </div>
  );
};

const ProductCategoryLinks = ({
  setSideNavOpen,
}: {
  setSideNavOpen: (value: boolean) => void;
}) => {
  const router = useRouter();
  const domainCountryCode = useSelector(getDomainCountryCode);

  const localeNavLinks = NAV_LINKS.filter(
    (link) =>
      !(
        (link.hideFromCA && domainCountryCode === "CA") ||
        (link.hideFromUS && domainCountryCode !== "CA")
      ),
  );
  const isIos = hasIosParam(router);
  const localeDeviceNavLinks = localeNavLinks.filter(
    (link) => !(isIos && link.hideFromIOS),
  );

  const onClick = (
    eventName: keyof typeof GTM_EVENTS.TOP_NAV,
    eventLabel?: string,
  ) => trackGtmEvent(GTM_EVENTS.TOP_NAV[eventName], { eventLabel });
  return (
    <div className="bg-leafly-white relative">
      <div className="container overflow-x-auto py-md ml-none mr-lg md:mx-auto px-lg md:px-none no-scrollbar fade-leafly-white-right">
        <nav
          aria-label="Product category"
          className="flex gap-5 items-center text-sm"
        >
          <button
            className="flex items-center hover:underline flex-shrink-0 text-default"
            data-testid="topnav-categories-button"
            onClick={() => {
              setSideNavOpen(true);
              onClick("MORE_CLICK");
            }}
          >
            <span>Categories</span>
            <span>
              <SolidArrowIcon width="24" height="24" />
            </span>
          </button>
          {localeDeviceNavLinks.map((link) => {
            const { name, href } = link;
            return (
              <Link
                className="hover:underline flex-shrink-0 text-default"
                data-testid="topnav-link"
                href={href}
                onClick={() => onClick("NAV_LINK_CLICK", href)}
                key={name}
              >
                {name}
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export const ProductCategoryNavigation: React.FC<{
  categories: ProductMarketplaceCategory[];
  currentCategory?: ProductMarketplaceCategory;
}> = ({ categories, currentCategory }) => {
  const [isSideNavOpen, setSideNavOpen] = useState(false);

  return (
    <>
      <Overlay isSideNavOpen={isSideNavOpen} setSideNavOpen={setSideNavOpen} />
      <SideNavigation
        categories={categories}
        currentCategory={currentCategory}
        isSideNavOpen={isSideNavOpen}
        setSideNavOpen={setSideNavOpen}
      />
      <ProductCategoryLinks setSideNavOpen={setSideNavOpen} />
    </>
  );
};
